import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Plus, Shield, Mail, Phone, Key, 
  Edit3, Trash2, X, Check, RefreshCw, 
  Crown, Lock, Eye, EyeOff, AlertTriangle,
  ChevronDown, ChevronUp
} from 'lucide-react';
import { adminAPI } from '../../api';
import toast from 'react-hot-toast';

const SubAdminManagement = () => {
  const [subAdmins, setSubAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState({});
  const [allPermissions, setAllPermissions] = useState([]);
  const [myPermissions, setMyPermissions] = useState(null);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    permissions: []
  });
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [adminsRes, permsRes, myPermsRes] = await Promise.all([
        adminAPI.getSubAdmins(),
        adminAPI.getAvailablePermissions(),
        adminAPI.getMyPermissions()
      ]);
      
      setSubAdmins(adminsRes.data.sub_admins || []);
      setPermissions(permsRes.data.permissions || {});
      setAllPermissions(permsRes.data.all_permissions || []);
      setMyPermissions(myPermsRes.data);
      
      // Expand all categories by default
      const expanded = {};
      Object.keys(permsRes.data.permissions || {}).forEach(cat => {
        expanded[cat] = true;
      });
      setExpandedCategories(expanded);
    } catch (error) {
      toast.error('Failed to load sub-admin data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateAdmin = async () => {
    if (!formData.email || !formData.password || !formData.name) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      setSaving(true);
      await adminAPI.createSubAdmin(formData);
      toast.success('Sub-admin created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create sub-admin');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateAdmin = async () => {
    if (!selectedAdmin) return;
    
    try {
      setSaving(true);
      await adminAPI.updateSubAdmin(selectedAdmin.admin_id, {
        name: formData.name,
        phone: formData.phone,
        permissions: formData.permissions,
        is_active: formData.is_active
      });
      toast.success('Sub-admin updated successfully');
      setShowEditModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update sub-admin');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAdmin = async () => {
    if (!selectedAdmin) return;
    
    try {
      setSaving(true);
      await adminAPI.deleteSubAdmin(selectedAdmin.admin_id);
      toast.success('Sub-admin deleted successfully');
      setShowDeleteModal(false);
      setSelectedAdmin(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete sub-admin');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedAdmin || !newPassword) return;
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      setSaving(true);
      await adminAPI.resetSubAdminPassword(selectedAdmin.admin_id, { new_password: newPassword });
      toast.success('Password reset successfully');
      setShowPasswordModal(false);
      setNewPassword('');
      setSelectedAdmin(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      name: '',
      phone: '',
      permissions: []
    });
    setSelectedAdmin(null);
  };

  const openEditModal = (admin) => {
    setSelectedAdmin(admin);
    setFormData({
      email: admin.email,
      name: admin.name,
      phone: admin.phone || '',
      permissions: admin.permissions || [],
      is_active: admin.is_active
    });
    setShowEditModal(true);
  };

  const togglePermission = (permKey) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permKey)
        ? prev.permissions.filter(p => p !== permKey)
        : [...prev.permissions, permKey]
    }));
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const selectAllInCategory = (category) => {
    const categoryPerms = permissions[category]?.map(p => p.key) || [];
    const allSelected = categoryPerms.every(p => formData.permissions.includes(p));
    
    setFormData(prev => ({
      ...prev,
      permissions: allSelected
        ? prev.permissions.filter(p => !categoryPerms.includes(p))
        : [...new Set([...prev.permissions, ...categoryPerms])]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Check if current user is super admin
  const isSuperAdmin = myPermissions?.is_super_admin;

  if (!isSuperAdmin) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-8 text-center">
        <Shield className="w-12 h-12 text-amber-600 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-amber-900 mb-2">Access Restricted</h2>
        <p className="text-amber-700">Only Super Admins can manage sub-administrators.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2 sm:gap-3">
            <Users className="w-5 h-5 sm:w-7 sm:h-7 text-purple-600 flex-shrink-0" />
            <span className="truncate">Sub-Admin Management</span>
          </h1>
          <p className="text-sm text-slate-500 mt-1">Manage admin accounts with custom permissions</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-3 sm:px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 text-sm"
          data-testid="create-subadmin-btn"
        >
          <Plus className="w-4 h-4" />
          <span>Add Sub-Admin</span>
        </button>
      </div>

      {/* Current User Info */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-100 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
            <Crown className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-medium text-slate-900">Logged in as: {myPermissions?.email}</p>
            <p className="text-sm text-purple-600">Role: Super Admin (Full Access)</p>
          </div>
        </div>
      </div>

      {/* Sub-Admins List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100 bg-slate-50">
          <h3 className="font-semibold text-slate-900">All Administrators ({subAdmins.length})</h3>
        </div>
        
        {subAdmins.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>No sub-admins created yet</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {subAdmins.map((admin) => (
              <div
                key={admin.admin_id}
                className={`p-3 sm:p-4 hover:bg-slate-50 transition-colors ${!admin.is_active ? 'opacity-60' : ''}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      admin.role === 'super_admin' ? 'bg-purple-100' : 'bg-blue-100'
                    }`}>
                      {admin.role === 'super_admin' ? (
                        <Crown className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                      ) : (
                        <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="font-medium text-slate-900 text-sm sm:text-base">{admin.name}</h4>
                        {admin.role === 'super_admin' && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                            Super Admin
                          </span>
                        )}
                        {!admin.is_active && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                            Disabled
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mt-1 text-sm text-slate-500">
                        <span className="flex items-center gap-1 truncate">
                          <Mail className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{admin.email}</span>
                        </span>
                        {admin.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{admin.phone}</span>
                          </span>
                        )}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {admin.role === 'super_admin' ? (
                          <span className="text-xs text-purple-600">All permissions</span>
                        ) : (
                          admin.permissions?.slice(0, 4).map(perm => (
                            <span key={perm} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded">
                              {perm.replace(/_/g, ' ')}
                            </span>
                          ))
                        )}
                        {admin.role !== 'super_admin' && admin.permissions?.length > 4 && (
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded">
                            +{admin.permissions.length - 4} more
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {admin.role !== 'super_admin' && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEditModal(admin)}
                        className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => { setSelectedAdmin(admin); setShowPasswordModal(true); }}
                        className="p-2 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                        title="Reset Password"
                      >
                        <Key className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => { setSelectedAdmin(admin); setShowDeleteModal(true); }}
                        className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
                <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Plus className="w-5 h-5 text-purple-600" />
                  Create Sub-Admin
                </h3>
                <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Basic Info */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="admin@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Password *</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg pr-10"
                        placeholder="Min 6 characters"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Admin Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Phone (for OTP)</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="9876543210"
                    />
                  </div>
                </div>

                {/* Permissions */}
                <div className="border-t pt-4">
                  <h4 className="font-medium text-slate-900 mb-3">Permissions</h4>
                  <div className="space-y-2">
                    {Object.entries(permissions).map(([category, perms]) => (
                      <div key={category} className="border rounded-lg overflow-hidden">
                        <button
                          onClick={() => toggleCategory(category)}
                          className="w-full px-4 py-3 bg-slate-50 flex items-center justify-between"
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-700">{category}</span>
                            <span className="text-xs text-slate-500">
                              ({formData.permissions.filter(p => perms.some(perm => perm.key === p)).length}/{perms.length})
                            </span>
                          </div>
                          {expandedCategories[category] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        
                        {expandedCategories[category] && (
                          <div className="p-3 space-y-2">
                            <button
                              onClick={() => selectAllInCategory(category)}
                              className="text-xs text-blue-600 hover:underline"
                            >
                              {perms.every(p => formData.permissions.includes(p.key)) ? 'Deselect All' : 'Select All'}
                            </button>
                            <div className="grid md:grid-cols-2 gap-2">
                              {perms.map((perm) => (
                                <label
                                  key={perm.key}
                                  className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                                    formData.permissions.includes(perm.key) ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50 border border-transparent hover:bg-slate-100'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={formData.permissions.includes(perm.key)}
                                    onChange={() => togglePermission(perm.key)}
                                    className="mt-1"
                                  />
                                  <div>
                                    <p className="text-sm font-medium text-slate-700">{perm.name}</p>
                                    <p className="text-xs text-slate-500">{perm.description}</p>
                                  </div>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="p-6 border-t border-slate-100 flex justify-end gap-3 sticky bottom-0 bg-white">
                <button
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                  className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateAdmin}
                  disabled={saving}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 disabled:opacity-50"
                >
                  {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Create Sub-Admin
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Edit Modal */}
      <AnimatePresence>
        {showEditModal && selectedAdmin && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
                <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Edit3 className="w-5 h-5 text-blue-600" />
                  Edit Sub-Admin: {selectedAdmin.email}
                </h3>
                <button onClick={() => { setShowEditModal(false); resetForm(); }} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Basic Info */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                </div>
                
                {/* Active Status */}
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      className="w-4 h-4"
                    />
                    <span className="font-medium text-slate-700">Active</span>
                  </label>
                  <span className="text-sm text-slate-500">Disabled admins cannot log in</span>
                </div>

                {/* Permissions */}
                <div className="border-t pt-4">
                  <h4 className="font-medium text-slate-900 mb-3">Permissions</h4>
                  <div className="space-y-2">
                    {Object.entries(permissions).map(([category, perms]) => (
                      <div key={category} className="border rounded-lg overflow-hidden">
                        <button
                          onClick={() => toggleCategory(category)}
                          className="w-full px-4 py-3 bg-slate-50 flex items-center justify-between"
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-700">{category}</span>
                            <span className="text-xs text-slate-500">
                              ({formData.permissions.filter(p => perms.some(perm => perm.key === p)).length}/{perms.length})
                            </span>
                          </div>
                          {expandedCategories[category] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        
                        {expandedCategories[category] && (
                          <div className="p-3 space-y-2">
                            <button
                              onClick={() => selectAllInCategory(category)}
                              className="text-xs text-blue-600 hover:underline"
                            >
                              {perms.every(p => formData.permissions.includes(p.key)) ? 'Deselect All' : 'Select All'}
                            </button>
                            <div className="grid md:grid-cols-2 gap-2">
                              {perms.map((perm) => (
                                <label
                                  key={perm.key}
                                  className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                                    formData.permissions.includes(perm.key) ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50 border border-transparent hover:bg-slate-100'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={formData.permissions.includes(perm.key)}
                                    onChange={() => togglePermission(perm.key)}
                                    className="mt-1"
                                  />
                                  <div>
                                    <p className="text-sm font-medium text-slate-700">{perm.name}</p>
                                    <p className="text-xs text-slate-500">{perm.description}</p>
                                  </div>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="p-6 border-t border-slate-100 flex justify-end gap-3 sticky bottom-0 bg-white">
                <button
                  onClick={() => { setShowEditModal(false); resetForm(); }}
                  className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateAdmin}
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
                >
                  {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Save Changes
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && selectedAdmin && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl max-w-md w-full p-6"
            >
              <div className="text-center">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Delete Sub-Admin?</h3>
                <p className="text-slate-500 mb-6">
                  Are you sure you want to delete <strong>{selectedAdmin.email}</strong>? 
                  This will disable their admin access.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => { setShowDeleteModal(false); setSelectedAdmin(null); }}
                    className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteAdmin}
                    disabled={saving}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    Delete
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Password Reset Modal */}
      <AnimatePresence>
        {showPasswordModal && selectedAdmin && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl max-w-md w-full p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-amber-600" />
                  Reset Password
                </h3>
                <button onClick={() => { setShowPasswordModal(false); setNewPassword(''); setSelectedAdmin(null); }} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <p className="text-slate-500 mb-4">
                Set a new password for <strong>{selectedAdmin.email}</strong>
              </p>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-1">New Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg pr-10"
                    placeholder="Min 6 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowPasswordModal(false); setNewPassword(''); setSelectedAdmin(null); }}
                  className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetPassword}
                  disabled={saving || newPassword.length < 6}
                  className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                  Reset Password
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SubAdminManagement;
